import React, { useContext } from 'react';
import {
  Card,
  CardContent,
  CardFootnote,
  Paper,
  Image
} from '@thd-olt-component-react/core-ui';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  params,
  shape,
  fragment,
  customType,
  string,
  number as numberType,
  useDataModel
} from '@thd-nucleus/data-sources';
import classNames from 'classnames/bind';
import styles from './visual-navigation.scss';

const VisualNavigation = ({ componentId, componentClass, componentPosition }) => {
  const { channel } = useContext(ExperienceContext);
  const { data, loading, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass: 'VisualNavigation'
    }
  });

  if (!data || loading || error) {
    return null;
  }
  const cx = classNames.bind(styles);
  const visNavList = data?.component?.visualNavigationList;
  const isMobile = (channel === 'mobile');
  const visNavItem = (item) => {
    const marketingData = item?.marketingData;
    let link = item?.link;
    let description = item?.description;
    let title = item?.title;
    let image = item?.image;
    let altText = item?.image.description;

    if (marketingData) {
      link = marketingData.link;
      description = marketingData.description;
      altText = marketingData.silohImage?.damDownloadedContent?.description;
      title = marketingData.title_LessThan40Char;
      image = marketingData.silohImage?.damDownloadedContent;
    }

    return (
      <Card
        className={cx('card-tile__image--frame card-tile__item')}
        key={item?.id}
      >
        <CardContent
          noPadding
          className={cx('card-tile__content')}
        >
          <Paper
            border
            sharp
            className={cx('card-tile__paper--border')}
          >
            <a
              href={link || description}
              target="_blank"
              rel="noreferrer"
            >
              <Image
                alt={altText || title}
                title={altText || title}
                src={image?.url}
                className={cx('card-tile__image')}
                height={1}
                width={1}
              />
            </a>
          </Paper>
        </CardContent>
        <h3>
          <CardFootnote
            noPadding
            className={cx('card-tile__title')}
            link={{ label: title, href: link || description }}
          />
        </h3>
      </Card>
    );
  };
  return (
    <div
      className={cx('card-tile__collection')}
      id={`${componentClass}-${componentPosition}-${componentId}`}
      data-testid="visual-navigation"
      data-component-id={componentId}
      data-component-name={componentClass}
      data-component-position={componentPosition}
    >
      {visNavList.map((item) => (visNavItem(item)))}
    </div>
  );
};

VisualNavigation.displayName = 'VisualNavigation';
VisualNavigation.propTypes = {
  componentId: PropTypes.string.isRequired,
  componentClass: PropTypes.string,
  componentPosition: PropTypes.number,
};

VisualNavigation.defaultProps = {
  componentClass: '',
  componentPosition: 1,
};

VisualNavigation.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass').enum(['VisualNavigation']).isRequired(),
  }).shape({
    VisualNavigation: fragment().shape({
      id: string(),
      title: string(),
      visualNavigationList: shape({
        id: string(),
        title: string(),
        description: string(),
        link: string(),
        image: shape({
          id: string(),
          url: string(),
          contentType: string(),
          title: string(),
          description: string(),
          details: shape({
            height: numberType(),
            width: numberType(),
            size: numberType(),
          }),
        }),
        marketingData: shape({
          link: string(),
          lifestyleImage: shape({
            damDownloadedContent: shape({
              id: string(),
              url: string(),
              contentType: string(),
              title: string(),
              description: string(),
              details: shape({
                height: numberType(),
                width: numberType(),
                size: numberType(),
              }),
            }),
          }),
          title_LessThan50Char: string(),
          title_LessThan40Char: string(),
          description: string(),
          cta: string(),
          eyebrow_LessThan30Char: string(),
          legal: string(),
          badge: shape({
            damDownloadedContent: shape({
              id: string(),
              url: string(),
              contentType: string(),
              title: string(),
              description: string(),
              details: shape({
                height: numberType(),
                width: numberType(),
                size: numberType(),
              }),
            }),
          }),
          silohImage: shape({
            damDownloadedContent: shape({
              id: string(),
              url: string(),
              contentType: string(),
              title: string(),
              description: string(),
              details: shape({
                height: numberType(),
                width: numberType(),
                size: numberType(),
              }),
            }),
          }),
        }),
      }),
    }),
  }),
};

export { VisualNavigation };
